import React from "react";
import { Image } from "react-konva";
import { SVGShape } from "../../../../types";
import { mergeRefs } from "../../../../utils/mergeRef";
import { getWindowKonvaObject } from "../../helpers/konva/getWindowKonvaObject";
import { ShapeCommonProps } from "../../types";
import { useShape } from "../ShapeContext/useShape";

export const SvgShape = React.forwardRef<
  React.ComponentRef<typeof Image>,
  ShapeCommonProps
>((commonProps, ref) => {
  const { shape, image, setImage } = useShape();

  const svgShape = shape as SVGShape;
  const shapeRef = React.useRef<React.ComponentRef<typeof Image>>(null);

  React.useEffect(() => {
    (async () => {
      if (shape.type === "svg") {
        let imgUrl = svgShape.path;
        if (svgShape.svgContent) {
          const blob = new Blob([svgShape.svgContent], {
            type: "image/svg+xml"
          });
          imgUrl = URL.createObjectURL(blob);
        } else if (svgShape.path.startsWith("https://cdn.gettactic.com")) {
          const response = await fetch(svgShape.path);
          const svgContent = await response.text();
          const blob = new Blob([svgContent], {
            type: "image/svg+xml"
          });
          imgUrl = URL.createObjectURL(blob);
        }

        const img = new window.Image();
        img.src = imgUrl;
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          setImage(img);
          getWindowKonvaObject().stages?.[0]?.fire?.("contentChange");
        };
      }
    })().then();
  }, [shape.type, (shape as SVGShape).path]);

  React.useEffect(() => {
    if (shape.type === "svg" && image) {
      const layer = shapeRef.current?.getLayer();
      if (layer) {
        layer.batchDraw();
      }
    }
  }, [shape.type, image]);

  return (
    <Image
      {...commonProps}
      image={image || undefined}
      width={shape.width}
      height={shape.height}
      scaleX={svgShape.scaleX || 1}
      scaleY={svgShape.scaleY || 1}
      opacity={shape.locked ? 0.5 : 1}
      imageSmoothingEnabled={true}
      perfectDrawEnabled={true}
      ref={mergeRefs([shapeRef, ref])}
    />
  );
});
